import React, { useState, useEffect } from 'react';
import {
  MessageSquare, ShoppingCart, Menu as MenuIcon, BarChart3,
  Plus, Search, Filter, CheckCircle, Clock, XCircle, AlertCircle, Settings, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DirectConnect = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');
  const locationId = localStorage.getItem('current_location_id');

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
      // Set up polling for real-time updates (every 10 seconds)
      const interval = setInterval(() => {
        fetchOrders();
      }, 10000);
      setPollingInterval(interval);
      return () => clearInterval(interval);
    } else if (activeTab === 'menu') {
      fetchMenu();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [activeTab]);

  const fetchOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/directconnect/orders${statusFilter ? `?status=${statusFilter}` : ''}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId ? { 'X-Location-Id': locationId } : {})
        }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      } else {
        if (!silent) setError('Failed to fetch orders');
      }
    } catch (err) {
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/directconnect/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId ? { 'X-Location-Id': locationId } : {}),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        await fetchOrders(true); // Silent refresh
        if (selectedOrder?.id === orderId) {
          const updated = await fetch(`${API_URL}/api/directconnect/orders/${orderId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Organization-Id': orgId || ''
            }
          });
          if (updated.ok) {
            setSelectedOrder(await updated.json());
          }
        }
      } else {
        setError('Failed to update order status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchOrderDetail = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/directconnect/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const order = await res.json();
        setSelectedOrder(order);
        setShowOrderDetail(true);
      }
    } catch (err) {
      setError('Failed to fetch order details');
    }
  };

  const fetchMenu = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/directconnect/menu`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setMenu(data);
      } else {
        setError('Failed to fetch menu');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/directconnect/analytics/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      } else {
        setError('Failed to fetch analytics');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      ready: 'bg-green-100 text-green-800',
      delivered: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'delivered' || status === 'confirmed') return <CheckCircle className="w-4 h-4" />;
    if (status === 'cancelled') return <XCircle className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">DirectConnect</h1>
        <p className="text-gray-600">WhatsApp ordering and customer engagement</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-5 h-5" />
              <span>Orders</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'menu'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <MenuIcon className="w-5 h-5" />
              <span>Menu</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5" />
              <span>Analytics</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {!loading && activeTab === 'orders' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select 
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  fetchOrders();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="text-sm text-gray-500">
              Auto-refreshing every 10s
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No orders yet. Orders from WhatsApp will appear here.
                    </td>
                  </tr>
                ) : (
                  orders
                    .filter(order => {
                      if (!searchTerm) return true;
                      const search = searchTerm.toLowerCase();
                      return (
                        (order.order_number || order.id || '').toLowerCase().includes(search) ||
                        (order.customer_name || '').toLowerCase().includes(search) ||
                        (order.customer_phone || '').includes(search)
                      );
                    })
                    .map((order) => (
                    <tr 
                      key={order.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => fetchOrderDetail(order.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.order_number || order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{order.customer_name}</div>
                          <div className="text-gray-500">{order.customer_phone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {order.items?.length || 0} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        £{order.total_amount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={order.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateOrderStatus(order.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={`text-xs font-medium rounded-full px-2.5 py-0.5 border-0 ${getStatusColor(order.status)} focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="preparing">Preparing</option>
                          <option value="ready">Ready</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === 'menu' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Menu Management</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => window.location.href = '/menu-items'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Manage Menu Items</span>
              </button>
              <button
                onClick={() => window.location.href = '/menu-config'}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                <Settings className="w-4 h-4" />
                <span>Menu Config</span>
              </button>
            </div>
          </div>

          {menu ? (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">{menu.name || 'Menu'}</h3>
              {menu.items && menu.items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menu.items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{item.description}</div>
                      <div className="text-lg font-semibold mt-2">£{item.price.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No menu items yet. Add items to get started.</p>
              )}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <MenuIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No menu configured yet.</p>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Create Menu
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && activeTab === 'analytics' && (
        <div>
          <h2 className="text-xl font-semibold mb-6">Analytics Summary</h2>
          {analytics ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm text-gray-500 mb-1">Total Orders</div>
                <div className="text-3xl font-bold text-gray-900">{analytics.total_orders || 0}</div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm text-gray-500 mb-1">Total Revenue</div>
                <div className="text-3xl font-bold text-gray-900">£{analytics.total_revenue?.toFixed(2) || '0.00'}</div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm text-gray-500 mb-1">Average Order Value</div>
                <div className="text-3xl font-bold text-gray-900">£{analytics.average_order_value?.toFixed(2) || '0.00'}</div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm text-gray-500 mb-1">Active Orders</div>
                <div className="text-3xl font-bold text-gray-900">
                  {(analytics.orders_by_status?.confirmed || 0) + (analytics.orders_by_status?.preparing || 0)}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No analytics data yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Order Details</h2>
              <button onClick={() => {
                setShowOrderDetail(false);
                setSelectedOrder(null);
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Order Number</label>
                  <p className="text-lg font-semibold">{selectedOrder.order_number || selectedOrder.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <select aria-label="Order Number"
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                    className={`mt-1 block w-full rounded-lg px-3 py-2 border-0 ${getStatusColor(selectedOrder.status)} focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="preparing">Preparing</option>
                    <option value="ready">Ready</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Customer</label>
                <p className="text-lg">{selectedOrder.customer_name}</p>
                <p className="text-gray-600">{selectedOrder.customer_phone}</p>
                {selectedOrder.delivery_address && (
                  <p className="text-sm text-gray-500 mt-1">{selectedOrder.delivery_address}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Order Items</label>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        {item.modifiers && item.modifiers.length > 0 && (
                          <p className="text-sm text-gray-500 mt-1">
                            {item.modifiers.map(m => m.name || m).join(', ')}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 mt-1">Qty: {item.quantity} × £{item.unit_price?.toFixed(2)}</p>
                      </div>
                      <p className="font-semibold">£{item.subtotal?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>£{selectedOrder.subtotal?.toFixed(2) || '0.00'}</span>
                </div>
                {selectedOrder.tax_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span>£{selectedOrder.tax_amount?.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span>£{selectedOrder.delivery_fee?.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-£{selectedOrder.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>£{selectedOrder.total_amount?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-500">Payment Status</label>
                  <p className="font-medium capitalize">{selectedOrder.payment_status || 'pending'}</p>
                </div>
                <div>
                  <label className="text-gray-500">Delivery Type</label>
                  <p className="font-medium capitalize">{selectedOrder.delivery_type || 'pickup'}</p>
                </div>
                <div>
                  <label className="text-gray-500">Created</label>
                  <p>{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : '-'}</p>
                </div>
                {selectedOrder.updated_at && (
                  <div>
                    <label className="text-gray-500">Last Updated</label>
                    <p>{new Date(selectedOrder.updated_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectConnect;
